import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { cloneDeep, forEach, sortBy } from 'lodash';
import { useLazyQuery } from '@apollo/client';

import { GET_ORDER_BY_ORDER_ID_AND_EMAIL } from 'mocks/queries';
import avo from 'analytics/avo';

import { EMAIL_REGEX } from 'commons/constants';
import CtaButton from 'components/essentials/Cta/CtaButton';
import LabeledTextInput from 'components/consumer/LabeledTextInput';
import useUserData from 'data-hooks/useUserData';

const sortIssueTypes = orderInfo => {
  const orderDetails = cloneDeep(orderInfo);
  if (orderDetails.orderCartIssues) {
    forEach(orderDetails.orderCartIssues, issue => {
      issue.categories = sortBy(issue.categories, 'id');
      forEach(issue.categories, category => {
        category.issues = sortBy(category.issues, 'id');
        forEach(category.issues, component => {
          component.components = sortBy(component.components, 'id');
        });
      });
    });
  }
  return orderDetails;
};

const OrderLookupForm = ({
  actions: { updateOrderInfo, updateOrderLookupInfo },
}) => {
  const [currentUser, updateCurrentUser] = useState(null);
  const [errorMsg, updateErrorMsg] = useState(null);

  const { loading: loadingIsUserLoggedIn, user } = useUserData();

  const [
    getOrderByOrderIdAndEmail,
    { loading: orderLookupLoading, data: orderLookupData, called },
  ] = useLazyQuery(GET_ORDER_BY_ORDER_ID_AND_EMAIL);

  useEffect(() => {
    if (
      called &&
      !orderLookupLoading &&
      orderLookupData?.getOrderByOrderIdAndEmail
    ) {
      if (orderLookupData.getOrderByOrderIdAndEmail?.errors) {
        const err =
          orderLookupData.getOrderByOrderIdAndEmail.errors &&
          orderLookupData.getOrderByOrderIdAndEmail.errors.length > 0
            ? orderLookupData.getOrderByOrderIdAndEmail.errors[0].message
            : 'The e-mail address does not match the order number provided. Please double check and try again.';
        updateErrorMsg(err);
      }
      if (orderLookupData.getOrderByOrderIdAndEmail?.order) {
        updateErrorMsg(null);
        updateOrderInfo(
          sortIssueTypes(orderLookupData.getOrderByOrderIdAndEmail)
        );
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderLookupLoading, orderLookupData, called]);

  useEffect(() => {
    if (!loadingIsUserLoggedIn && user?.id) {
      updateCurrentUser(user);
    }
  }, [loadingIsUserLoggedIn, user]);

  return (
    <Formik
      name="warrantyIssueStep1"
      initialValues={
        currentUser?.email
          ? { email: currentUser?.email, order_id: '' }
          : {
              email: '',
              order_id: '',
            }
      }
      validationSchema={Yup.object().shape({
        order_id: Yup.string()
          .required('Order number is required')
          .matches(
            /((^(J|j{1})(-{1})([0-9]{1,})$)|(^[0-9]{1,}$))/,
            'Invalid order id'
          ),
        email: Yup.string()
          .required('Email address is required')
          .matches(EMAIL_REGEX, 'Invalid email'),
      })}
      onSubmit={({ order_id, email }) => {
        const orderId = parseInt(order_id, 10);
        updateOrderLookupInfo({
          order_id: orderId,
          email,
        });
        avo.warrantyFormStarted({
          email,
          orderId: orderId.toString(),
        });
        getOrderByOrderIdAndEmail({
          variables: {
            order_id: orderId,
            email,
          },
        });
      }}
      enableReinitialize
      render={({
        values,
        errors,
        touched,
        handleSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
      }) => (
        <div className="[&_button]:w-full [&_button]:mt-4">
          <form data-xc="warranty-issue-step1-form" onSubmit={handleSubmit}>
            <LabeledTextInput
              name="order_id"
              value={values.order_id}
              prefixText="J-"
              prefixWidth={16}
              label="Order Number"
              onBlur={handleBlur}
              errorMessage={
                errors.order_id &&
                touched.order_id && (
                  <span
                    data-xc="warranty-order_id-validation"
                    className="text-xs text-red py-0.5"
                  >
                    {errors.order_id}
                  </span>
                )
              }
              onChange={evt => {
                const val = evt.target.value;
                // replace all except numbers
                setFieldValue(
                  'order_id',
                  val ? val.replace(/[^0-9]/g, '') : ''
                );
                updateErrorMsg(null);
              }}
              data-xc="warranty-order_id-field"
            />

            <LabeledTextInput
              type="email"
              name="email"
              value={values.email}
              label="Email"
              onBlur={handleBlur}
              onChange={evt => {
                handleChange(evt);
                updateErrorMsg(null);
              }}
              errorMessage={
                errors.email &&
                touched.email && (
                  <span
                    data-xc="warranty-email-validation"
                    className="text-xs text-red py-0.5"
                  >
                    {errors.email}
                  </span>
                )
              }
            />

            {!!errorMsg && (
              <div
                data-xc="warranty-form-validation"
                className="text-red py-2 px-3.5 my-4 border-[1px] border-[#d00] bg-[#FFEBE8] text-sm"
              >
                {errorMsg}
              </div>
            )}

            <CtaButton
              type="submit"
              color="black"
              noLinkPadding
              hasArrow={false}
              styling="solid-button"
              data-xc="issue-submit-btn"
              disabled={orderLookupLoading}
              isLoading={!!orderLookupLoading}
            >
              Start Your Request
            </CtaButton>
          </form>
        </div>
      )}
    />
  );
};

OrderLookupForm.propTypes = {
  actions: PropTypes.shape({
    updateOrderInfo: PropTypes.func,
    updateOrderLookupInfo: PropTypes.func,
  }),
};
export default OrderLookupForm;
